import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { EventType, EventTypeSearchParams } from '../../services/eventTypeApi';

export interface EventTypeState {
  // Data
  items: EventType[];
  selectedItem: EventType | null;
  
  // UI State
  loading: boolean;
  error: string | null;
  
  // Form State
  formData: {
    Name: string;
    Description: string;
    Active: boolean;
  };
  
  // Validation
  validationErrors: string[];
  
  // Search and Filter
  searchParams: EventTypeSearchParams;
  
  // Pagination
  pagination: {
    page: number;
    pageSize: number;
    total: number;
  };
  
  // Statistics
  statistics: {
    total: number;
    active: number;
    inactive: number;
  };
  
  // Bulk Operations
  selectedIds: number[];
  bulkOperationLoading: boolean;
  
  // Success Messages
  successMessage: string;
}

const initialState: EventTypeState = {
  // Data
  items: [],
  selectedItem: null,
  
  // UI State
  loading: false,
  error: null,
  
  // Form State
  formData: {
    Name: '',
    Description: '',
    Active: true,
  },
  
  // Validation
  validationErrors: [],
  
  // Search and Filter
  searchParams: {
    page: 1,
    pageSize: 10,
    search: '',
    active: undefined,
    sortBy: 'Name',
    sortOrder: 'asc',
  },
  
  // Pagination
  pagination: {
    page: 1,
    pageSize: 10,
    total: 0,
  },
  
  // Statistics
  statistics: {
    total: 0,
    active: 0,
    inactive: 0,
  },
  
  // Bulk Operations
  selectedIds: [],
  bulkOperationLoading: false,
  
  // Success Messages
  successMessage: '',
};

const eventTypeSlice = createSlice({
  name: 'eventType',
  initialState,
  reducers: {
    // Loading states
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    
    // Error handling
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    
    // Data management
    setItems: (state, action: PayloadAction<EventType[]>) => {
      state.items = action.payload;
    },
    setSelectedItem: (state, action: PayloadAction<EventType | null>) => {
      state.selectedItem = action.payload;
    },
    addItem: (state, action: PayloadAction<EventType>) => {
      state.items.unshift(action.payload);
    },
    updateItem: (state, action: PayloadAction<EventType>) => {
      const index = state.items.findIndex(item => item.Id === action.payload.Id);
      if (index !== -1) {
        state.items[index] = action.payload;
      }
      if (state.selectedItem?.Id === action.payload.Id) {
        state.selectedItem = action.payload;
      }
    },
    removeItem: (state, action: PayloadAction<number>) => {
      state.items = state.items.filter(item => item.Id !== action.payload);
      if (state.selectedItem?.Id === action.payload) {
        state.selectedItem = null;
      }
    },
    
    // Form management
    setFormData: (state, action: PayloadAction<Partial<EventTypeState['formData']>>) => {
      state.formData = { ...state.formData, ...action.payload };
    },
    resetFormData: (state) => {
      state.formData = initialState.formData;
    },
    
    // Validation
    setValidationErrors: (state, action: PayloadAction<string[]>) => {
      state.validationErrors = action.payload;
    },
    clearValidationErrors: (state) => {
      state.validationErrors = [];
    },
    
    // Search and filter
    setSearchParams: (state, action: PayloadAction<Partial<EventTypeSearchParams>>) => {
      state.searchParams = { ...state.searchParams, ...action.payload };
    },
    resetSearchParams: (state) => {
      state.searchParams = initialState.searchParams;
    },
    
    // Pagination
    setPagination: (state, action: PayloadAction<Partial<EventTypeState['pagination']>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    
    // Statistics
    setStatistics: (state, action: PayloadAction<EventTypeState['statistics']>) => {
      state.statistics = action.payload;
    },
    
    // Bulk operations
    setSelectedIds: (state, action: PayloadAction<number[]>) => {
      state.selectedIds = action.payload;
    },
    addSelectedId: (state, action: PayloadAction<number>) => {
      if (!state.selectedIds.includes(action.payload)) {
        state.selectedIds.push(action.payload);
      }
    },
    removeSelectedId: (state, action: PayloadAction<number>) => {
      state.selectedIds = state.selectedIds.filter(id => id !== action.payload);
    },
    clearSelectedIds: (state) => {
      state.selectedIds = [];
    },
    setBulkOperationLoading: (state, action: PayloadAction<boolean>) => {
      state.bulkOperationLoading = action.payload;
    },
    
    // Success messages
    setSuccessMessage: (state, action: PayloadAction<string>) => {
      state.successMessage = action.payload;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = '';
    },
    
    // Reset state
    resetEventTypeState: () => initialState,
  },
});

// Action creators
export const {
  setLoading,
  setError,
  setItems,
  setSelectedItem,
  addItem,
  updateItem,
  removeItem,
  setFormData,
  resetFormData,
  setValidationErrors,
  clearValidationErrors,
  setSearchParams,
  resetSearchParams,
  setPagination,
  setStatistics,
  setSelectedIds,
  addSelectedId,
  removeSelectedId,
  clearSelectedIds,
  setBulkOperationLoading,
  setSuccessMessage,
  clearSuccessMessage,
  resetEventTypeState,
} = eventTypeSlice.actions;

// Saga action types
export const FETCH_EVENT_TYPES = 'eventType/fetchEventTypes';
export const FETCH_EVENT_TYPE_BY_ID = 'eventType/fetchEventTypeById';
export const CREATE_EVENT_TYPE = 'eventType/createEventType';
export const UPDATE_EVENT_TYPE = 'eventType/updateEventType';
export const DELETE_EVENT_TYPE = 'eventType/deleteEventType';
export const SEARCH_EVENT_TYPES = 'eventType/searchEventTypes';
export const FETCH_STATISTICS = 'eventType/fetchStatistics';
export const BULK_ACTIVATE = 'eventType/bulkActivate';
export const BULK_DEACTIVATE = 'eventType/bulkDeactivate';
export const BULK_DELETE = 'eventType/bulkDelete';
export const EXPORT_TO_CSV = 'eventType/exportToCSV';

// Saga action creators
export const fetchEventTypes = (params?: EventTypeSearchParams) => ({ type: FETCH_EVENT_TYPES, payload: params });
export const fetchEventTypeById = (id: number) => ({ type: FETCH_EVENT_TYPE_BY_ID, payload: id });
export const createEventType = (eventType: Omit<EventType, 'Id'>) => ({ type: CREATE_EVENT_TYPE, payload: eventType });
export const updateEventType = (id: number, eventType: Partial<EventType>) => ({ type: UPDATE_EVENT_TYPE, payload: { id, eventType } });
export const deleteEventType = (id: number) => ({ type: DELETE_EVENT_TYPE, payload: id });
export const searchEventTypes = (params: EventTypeSearchParams) => ({ type: SEARCH_EVENT_TYPES, payload: params });
export const fetchStatistics = () => ({ type: FETCH_STATISTICS });
export const bulkActivate = (ids: number[]) => ({ type: BULK_ACTIVATE, payload: ids });
export const bulkDeactivate = (ids: number[]) => ({ type: BULK_DEACTIVATE, payload: ids });
export const bulkDelete = (ids: number[]) => ({ type: BULK_DELETE, payload: ids });
export const exportToCSV = (params?: EventTypeSearchParams) => ({ type: EXPORT_TO_CSV, payload: params });

export default eventTypeSlice.reducer; 